import { useActiveDocumentInfoContext } from '../../../shared/hooks/ActiveDocumentProvider';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { snapshotCurrentPcfSettingsStoreForDebug } from '../../../shared/api/pipeex/pcfSettingsClient';
import {
  describeValidationContext,
  isValidationReportCurrent,
} from '../lib/validationContextFingerprint';
import { filterSelectableValidationIds } from '../lib/validationReportNormalizer';
import { buildValidationMappingOverridesFromStore } from '../lib/validationMappingOverrides';
import type { PcfValidationReport, ValidationDefinition } from '../types';

export interface UseValidationReportCurrencyOptions {
  report: PcfValidationReport | null;
  documentInfo: any;
  validations: ValidationDefinition[];
  selectedValidations: number[];
  reconnectGapMarginMillimeters: number;
  /** Verified setup revision this setup is on, or `null` while nothing is verified. */
  verifiedRevision?: string | null;
}

/**
 * Answers whether the visible validation report still describes the live setup.
 *
 * The comparison mirrors what the runner stored: the same context descriptor is
 * rebuilt from the setup as it is now, so a report only counts as current while
 * every part of that context is unchanged.
 */
export function useValidationReportCurrency(options: UseValidationReportCurrencyOptions): boolean {
  const {
    report,
    documentInfo,
    validations,
    selectedValidations,
    reconnectGapMarginMillimeters,
    verifiedRevision = null,
  } = options;

  const activeDocumentInfo = useActiveDocumentInfoContext();
  const reportContextFingerprint = usePcfValidationStore((state) => state.reportContextFingerprint);
  const reportStale = usePcfValidationStore((state) => state.reportStale);

  // Nothing to compare against costs nothing to answer, so the settings
  // snapshot below is only read once a report actually claims a context.
  if (!report || reportStale || !reportContextFingerprint) {
    return false;
  }

  const { fingerprint } = describeValidationContext({
    activeProjectKey: activeDocumentInfo.projectKey ?? null,
    documentName: documentInfo?.DocumentName ?? null,
    documentPath: documentInfo?.DocumentPath ?? null,
    verifiedRevision,
    selectedValidationIds: filterSelectableValidationIds(selectedValidations, validations),
    settingsSnapshot: { ...snapshotCurrentPcfSettingsStoreForDebug() },
    reconnectGapMarginMillimeters,
    mappingOverrides: buildValidationMappingOverridesFromStore(),
  });

  return isValidationReportCurrent({ reportContextFingerprint, reportStale }, fingerprint);
}
